import { getDisplayPrice, getPriceToAdd, type ParsedAsset } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import dayjs from 'dayjs'
import { useRouter } from 'expo-router'
import { useCallback, useMemo } from 'react'
import { View } from 'react-native'
import { z } from 'zod/v4'
import { createFlowPhases, FlowProvider } from '~/components/flows/FlowProvider.tsx'
import { ConfirmationPhase } from '~/components/flows/phases/ConfirmationPhase'
import { DatesPhase, datesSchema } from '~/components/flows/phases/DatesPhase'
import { PhaseWrapper } from '~/components/flows/phases/PhaseWrapper.tsx'
import {
  PricingPhase,
  pricingSchema,
  type PricingData,
} from '~/components/flows/phases/PricingPhase'
import { useFlowManager } from '~/components/flows/useFlowManager'
import { useAlert } from '~/components/GlobalAlert.tsx'
import { ViewTitle } from '~/components/layout/Sections.tsx'
import { DisplayValue, Label } from '~/components/typography/Content.tsx'
import { Button } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import { useUserId } from '~/components/user/SessionProvider'
import { useAddAssetPrice, useConvertAssetRelation } from '~/data/assets/useAssets'
import { cn, getValidDate } from '~/lib/utils.ts'

const flowDataSchema = z.object({
  dates: datesSchema,
  pricing: pricingSchema,
})

export type ConvertPropertyFlowData = z.infer<typeof flowDataSchema>

export function ConvertPropertyFlow({ asset }: { asset: ParsedAsset }) {
  const { t } = useTranslations(['property', 'common'])
  const router = useRouter()
  const userId = useUserId()

  const onClose = useCallback(
    (resetFlow: () => void) => {
      resetFlow()

      if (router.canGoBack()) {
        router.back()
      } else {
        router.replace(`/(authenticated)/property/${asset.id}`)
      }
    },
    [router, asset.id]
  )

  const userRelation = useMemo(
    () => asset?.relations.find((r) => r.relation_type === 'representation'),
    [asset]
  )

  const displayPrice = getDisplayPrice(asset.prices || [])

  const { mutateAsync: convertRelation } = useConvertAssetRelation()
  const { mutateAsync: addAssetPrice } = useAddAssetPrice(asset.id)

  const { showAlert } = useAlert()

  const handleFlowComplete = useCallback(
    async (data: unknown) => {
      const parsedData = flowDataSchema.safeParse(data)
      if (!parsedData.success) {
        console.error('Invalid data:', parsedData.error)

        showAlert({
          title: t(($) => $.errors.conversionFailed),
          description: t(($) => $.errors.conversionFailedDescription),
          type: 'error',
        })

        return false
      }

      const flowData = parsedData.data

      if (!asset || !userId) {
        return false
      }

      try {
        // TODO: Convert from prospect to representation
        await convertRelation({
          userId,
          assetId: asset.id,
          fromRelation: 'representation',
          toRelation: 'reference',
          data: {
            saleDate: flowData.dates.saleDate,
            listingDate: flowData.dates.listingDate || userRelation?.data?.listingDate,
            listingLink: null,
          },
        })

        const askingPriceToAdd = getPriceToAdd(
          asset.prices || [],
          flowData.pricing.displayPrice ?? null,
          'asking_price'
        )

        const finalPriceToAdd = getPriceToAdd(
          asset.prices || [],
          flowData.pricing.finalPrice ?? null,
          'final_price'
        )

        if (askingPriceToAdd) {
          await addAssetPrice({
            price: askingPriceToAdd,
            priceType: 'asking_price',
          })
        }

        if (finalPriceToAdd) {
          await addAssetPrice({
            price: finalPriceToAdd,
            priceType: 'final_price',
          })
        }

        router.replace(`/(authenticated)/property/${asset.id}`)
        return true
      } catch (error) {
        console.error('Error converting property:', error)
        showAlert({
          title: t(($) => $.errors.conversionFailed),
          description: t(($) => $.errors.conversionFailedDescription),
          type: 'error',
        })
      }

      return false
    },
    [asset, convertRelation, userId, router, userRelation, addAssetPrice, showAlert, t]
  )

  const phases = useMemo(
    () =>
      createFlowPhases([
        {
          id: 'confirmation' as const,
          title: t(($) => $.flow.confirmConversion),
        },
        {
          id: 'dates' as const,
          title: t(($) => $.flow.saleDate),
          schema: datesSchema,
          data: {
            listingDate: userRelation?.data?.listingDate || '',
            saleDate: userRelation?.data?.saleDate || '',
          },
        },
        {
          id: 'pricing' as const,
          title: t(($) => $.flow.salePrice),
          schema: pricingSchema,
          data: {
            displayPrice: displayPrice?.price || 0,
            finalPrice: 0,
          } satisfies PricingData,
        },
        {
          id: 'review' as const,
          title: t(($) => $.flow.reviewAndSave),
        },
      ] as const),
    [userRelation?.data, displayPrice, t]
  )

  if (!userRelation) {
    return (
      <View className="flex-1 items-center justify-center gap-4">
        <Text className="text-center">{t(($) => $.flow.notFound)}</Text>
        <Button variant="primary" size="lg" onPress={() => router.back()}>
          {t(($) => $.common.navigation.previous)}
        </Button>
      </View>
    )
  }

  return (
    <FlowProvider
      flowId={`convert-property-${asset.id}`}
      phases={phases}
      onComplete={handleFlowComplete}
      onClose={onClose}
      flowTitle={t(($) => $.flow.convertToReference)}>
      <ConvertPhaseRenderer asset={asset} />
    </FlowProvider>
  )
}

function ConvertPhaseRenderer({ asset }: { asset: ParsedAsset }) {
  const { t } = useTranslations('property')
  const { data, updateData, currentPhaseId } = useFlowManager()

  switch (currentPhaseId) {
    case 'confirmation':
      return (
        <ConfirmationPhase
          title={t(($) => $.flow.convertToReference)}
          description={t(($) => $.view.conversionDescription)}
          asset={asset}
        />
      )
    case 'dates': {
      const datesData = data.dates as { listingDate: string; saleDate: string }
      return (
        <DatesPhase
          relationType="reference"
          dateValues={datesData}
          // eslint-disable-next-line i18next/no-literal-string -- technical property key
          onUpdateData={(updatedData) => updateData('dates', updatedData)}
        />
      )
    }
    case 'pricing': {
      const pricingData = data.pricing as PricingData
      return (
        <PricingPhase
          relationType="reference"
          priceValues={pricingData}
          // eslint-disable-next-line i18next/no-literal-string -- technical property key
          onUpdateData={(updatedData) => updateData('pricing', updatedData)}
        />
      )
    }
    case 'review':
      return <ConvertReviewPhase asset={asset} data={data as ConvertPropertyFlowData} />
    default:
      return (
        <View>
          <Text>
            {t(($) => $.flow.unknownPhase)}: {String(currentPhaseId)}
          </Text>
        </View>
      )
  }
}

function ConvertReviewPhase({
  asset,
  data,
}: {
  asset: ParsedAsset
  data: ConvertPropertyFlowData
}) {
  const { t } = useTranslations('property')
  const saleDate = getValidDate(data.dates.saleDate)
  const finalPrice = data.pricing.finalPrice

  return (
    <PhaseWrapper>
      <ViewTitle>{t(($) => $.flow.summary)}</ViewTitle>
      <View className="gap-4">
        <ReviewItem
          label={t(($) => $.flow.property)}
          value={asset.name || t(($) => $.view.notAvailable)}
          index={0}
        />
        <ReviewItem
          label={t(($) => $.flow.saleDate)}
          value={saleDate ? dayjs(saleDate).format('DD.MM.YYYY') : t(($) => $.view.notAvailable)}
          index={1}
        />
        <ReviewItem
          label={t(($) => $.flow.salePrice)}
          value={
            finalPrice ? `€${finalPrice.toLocaleString('fi-FI')}` : t(($) => $.view.notAvailable)
          }
          index={2}
        />
      </View>
    </PhaseWrapper>
  )
}

function ReviewItem({ label, value, index }: { label: string; value: string; index: number }) {
  return (
    <View className={cn('gap-1 rounded-lg px-4', index % 2 === 0 && 'bg-muted py-3')}>
      <Label>{label}:</Label>
      <DisplayValue>{value}</DisplayValue>
    </View>
  )
}
